import React, {ReactElement, useEffect, useMemo, useState} from "react";
import {Comparison} from "../../common/models/compare/comparison.model";
import {ComparisonCategoryResult} from "../../common/models/compare/comparisonCategoryResult";
import {QuizType} from "../../common/enums/quizType.enum";
import {Box, Divider, Stack, Typography, useTheme} from "@mui/material";
import {useIsMobile} from "../../common/hooks/useIsMobile";
import {ComparisonResultDetails} from "../../common/models/compare/comparisonResultDetails";
import {useCategoryComparison} from "../../common/hooks/useCategoryComparison";
import {SortHelper} from "../../common/utils/sort.helper";
import {GenericHelper} from "../../common/utils/generic.helper";

interface GenerateComparisonProps {
    comparison: Comparison;
    isCelebrityComparison?: boolean;
    onComparisonElementsGenerated?: () => void;
    animate?: boolean;
}

const GenerateComparisons = ({ comparison, isCelebrityComparison, onComparisonElementsGenerated, animate }: GenerateComparisonProps) => {
    
    const theme = useTheme();
    const isMobile = useIsMobile();
    const { getComparisonThemeColour } = useCategoryComparison();
    const baseDuration = 300;
    const [fadeElements, setFadeElements] = useState<{ id: string, timeout: number }[]>([]);
    const transitionBaseStyling = useMemo(() => { return { transition: theme.transitions.create(['opacity'], { duration: baseDuration, easing: 'linear'}), opacity: 0 }; }, []);
    const transitionTransitionedStyling = useMemo(() => { return { transition: theme.transitions.create(['opacity'], { duration: baseDuration, easing: 'linear'}), opacity: 1 }; }, []);
    const [transitionedElements, setTransitionedElements] = useState<{[key:string] : boolean}>({});
    const [comparisonElements, setComparisonElements] = useState<ReactElement[]>();

    if(!comparison) {
        return <></>;
    }

    const generateUserResults = (
        comparisonResultDetails: ComparisonResultDetails,
        comparisonCategoryResult: ComparisonCategoryResult,
        loveLanguageMatches: string[]
    ) => {

        switch (comparisonResultDetails.quizType) {
            case QuizType.StarSign: {
                switch (comparisonCategoryResult.categoryName) {
                    case "Sun Sign": {
                        return <Typography>☼ {comparisonCategoryResult.result}</Typography>;
                    }
                    case "Moon Sign": {
                        return <Typography>☾ {comparisonCategoryResult.result}</Typography>;
                    }
                    case "Rising Sign": {
                        return <Typography>↑ {comparisonCategoryResult.result}</Typography>;
                    }
                }

                return <Typography>{comparisonCategoryResult.result}</Typography>;
            }
            case QuizType.LoveLanguage: {
                return <Typography
                    fontWeight={loveLanguageMatches.includes(comparisonCategoryResult.result) ? "700" : undefined}
                >
                    {comparisonCategoryResult.result}
                </Typography>;
            }
            default: {
                return <Typography>{comparisonCategoryResult.result}</Typography>;
            }
        }
    };

    const generateComparisonResult = (
        comparisonResultDetails: ComparisonResultDetails,
        loveLanguageCount: number
    ) => {

        switch (comparisonResultDetails.quizType) {
            case QuizType.StarSign:{
                return (
                    <Typography
                        variant={"h4"}
                        color={getComparisonThemeColour(comparisonResultDetails.result, comparisonResultDetails.quizType)}
                        fontWeight={800}
                        textAlign={"center"}
                    >
                        {comparisonResultDetails.result}%
                    </Typography>
                );
            }
            case QuizType.MBTI:{
                return (
                    <Typography
                        variant={"h4"}
                        color={getComparisonThemeColour(comparisonResultDetails.result, comparisonResultDetails.quizType)}
                        fontWeight={800}
                        textAlign={"center"}
                    >
                        {comparisonResultDetails.result}
                    </Typography>
                );
            }
            case QuizType.LoveLanguage: {
                const loveHearts: ReactElement[] = [];
                for(let i = 0; i < loveLanguageCount; i++){
                    loveHearts.push(
                        <Typography
                            key={`match${i}`}
                            variant={"body1"}
                            fontWeight={700}
                            textAlign={"center"}
                        >
                            ❤️
                        </Typography>
                    );
                }

                return loveHearts;
            }
            default: {
                return (
                    <Typography
                        variant={"h4"}
                        fontWeight={800}
                    >
                        {comparisonResultDetails.result}
                    </Typography>
                );
            }
        }
    };

    useEffect(() => {
        
        if(!animate || !fadeElements || !fadeElements.length) {
            return;
        }

        GenericHelper.executeTimeouts(fadeElements, transitionedElements, setTransitionedElements);
    }, [animate, fadeElements, transitionedElements]);

    useEffect(() => {
        setTransitionedElements(prev => {
            return prev && prev.length ? prev : {};
        });
    }, [comparison]);

    useEffect(() => {

        const comparisonResultDetails = comparison.comparisonResultDetails.results;
        const elements:ReactElement[] = [];
        const loveLanguageMatches: string[] = [];
        const fadeTimeouts = [];
    
        for(let i = 0; i < comparisonResultDetails.length; i++)
        {
            const comparisonResult = comparisonResultDetails[i];
            const comparisonsFor = comparisonResultDetails[i].comparisonCategoryResultsFor ?? [];
            const comparisonsAgainst = comparisonResultDetails[i].comparisonCategoryResultsAgainst ?? [];
            const dataFor:ComparisonCategoryResult[] = [];
            const dataAgainst:ComparisonCategoryResult[] = [];
            const persona:string[] = [];
            const loveLanguages:string[] = [];
    
            if(isCelebrityComparison && (comparisonResult.mainCategoryTitle === "Persona" || comparisonResult.mainCategoryTitle === "Love Languages")){
                continue;
            }
    
            if(isCelebrityComparison && (comparisonResult.mainCategoryTitle === "Star Signs" && comparisonsAgainst.find(ca => ca.categoryName === "Sun Sign")?.result == null)) {
                continue;
            }
    
            for (let j = 0; j < comparisonsFor.length; j++)
            {
                const comparisonFor = comparisonsFor[j];
                const comparisonAgainst = comparisonsAgainst.find(ca => ca.categoryId === comparisonFor.categoryId);
    
                if(!comparisonAgainst) {
                    continue;
                }
    
                if(comparisonResult.mainCategoryTitle === "Persona")
                {
                    persona.push(comparisonFor.result, comparisonAgainst.result);
                    continue;
                }
    
                dataFor.push(comparisonFor);
                dataAgainst.push(comparisonAgainst);
    
                if(comparisonResult.quizType != QuizType.LoveLanguage)
                {
                    continue;
                }

                if(comparisonFor.result === comparisonAgainst.result)
                {
                    loveLanguageMatches.push(comparisonFor.result);
                    continue;
                }
    
                if(!loveLanguages.length)
                {
                    loveLanguages.push(comparisonFor.result, comparisonAgainst.result);
                    continue;
                }

                if(loveLanguages.includes(comparisonAgainst.result))
                {
                    loveLanguageMatches.push(comparisonAgainst.result);
                }
                else
                {
                    loveLanguages.push(comparisonAgainst.result);
                }
    
                if(loveLanguages.includes(comparisonFor.result))
                {
                    loveLanguageMatches.push(comparisonFor.result);
                    continue;
                }

                loveLanguages.push(comparisonFor.result);
            }
            
            const comparisonResultElementId = `comparison-result-${i}`;

            if(animate) {
                fadeTimeouts.push({
                    id: comparisonResultElementId,
                    timeout: baseDuration
                });
            }
    
            if(comparisonResult.mainCategoryTitle === "Persona")
            {
                elements.push(
                    <React.Fragment key={`comparison${i}`}>
                        <Divider sx={{ borderColor: theme.palette.thatsMe.dividersDark }} />
                        <Stack
                            id={comparisonResultElementId}
                            alignItems={"center"}
                            pt={3}
                            pb={5}
                            mx={isMobile? 2.5 : 0}
                            style={ !animate || transitionedElements[comparisonResultElementId] ? transitionTransitionedStyling : transitionBaseStyling }
                        >
                            <Typography
                                variant={"body1"}
                                textAlign={"center"}
                                color={theme.palette.thatsMe.textPrimary}
                            >
                                {
                                    persona.join(" + ")
                                } =
                            </Typography>
                            <Typography
                                variant={"h4"}
                                color={theme.palette.thatsMe.textPrimaryLight}
                                textAlign={"center"}
                                mt={1.5}
                            >
                                {comparisonResult.result}
                            </Typography>
                        </Stack>
                    </React.Fragment>
                );
                continue;
            }
    
            elements.push(
                <React.Fragment
                    key={`comparison${i}`}
                >
                    <Divider sx={{ borderColor: theme.palette.thatsMe.dividersDark }} />
                    <Stack
                        id={comparisonResultElementId}
                        flexDirection={"row"}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                        pt={3}
                        pb={3}
                        mx={0}
                        style={ !animate || transitionedElements[comparisonResultElementId] ? transitionTransitionedStyling : transitionBaseStyling }
                    >
                        <Stack
                            flex={1}
                            gap={1}
                        >
                            {
                                dataFor.sort(SortHelper.sortByProperty<ComparisonCategoryResult>('position')).map((comparisonCategoryResult, index) => (
                                        <Box
                                            key={`comparisonFor${index}`}
                                        >
                                            {
                                                generateUserResults(
                                                    comparisonResult,
                                                    comparisonCategoryResult,
                                                    loveLanguageMatches)
    
                                            }
                                        </Box>
                                    )
                                )
                            }
                        </Stack>
                        <Stack
                            flex={1}
                            alignItems={"center"}
                        >
                            {generateComparisonResult(comparisonResult, loveLanguageMatches.length)}
                        </Stack>
                        <Stack
                            flex={1}
                            gap={1}
                        >
                            {
                                dataAgainst.sort(SortHelper.sortByProperty<ComparisonCategoryResult>('position')).map((comparisonCategoryResult, index) => (
                                        <Stack
                                            key={`comparisonAgainst${index}`}
                                            alignItems={"flex-end"}
                                            textAlign={"end"}
                                        >
                                            {
                                                generateUserResults(
                                                    comparisonResult,
                                                    comparisonCategoryResult,
                                                    loveLanguageMatches
                                                )
                                            }
                                        </Stack>
                                    )
                                )
                            }
                        </Stack>
                    </Stack>
                </React.Fragment>
            );
        }

        if(animate) {
            setFadeElements(fadeTimeouts);
        }

        setComparisonElements(elements);

        onComparisonElementsGenerated && onComparisonElementsGenerated();

    }, [comparison, transitionBaseStyling, transitionTransitionedStyling, transitionedElements]);

    return <>
        {comparisonElements}
    </>;
};

export default GenerateComparisons